// angular
import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { interval, Subscription } from 'rxjs';
// components
import { TextSummarizerComponent } from '../text-summarizer/text-summarizer.component';
// types
import { IAuction } from '@app/+auctions/shared/types/auctions.type';
// pipes
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';
import { currencyToSymbol } from '@app/shared/utils/currency-to-symbol.util';

@Component({
  selector: 'pwj-auction-card',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    NgOptimizedImage,
    TranslateModule,
    TextSummarizerComponent,
    TranslateDataPipe,
    ImageSourcePipe,
  ],
  templateUrl: './auction-card.component.html',
  styleUrls: ['./auction-card.component.scss'],
})
export class AuctionCardComponent implements OnInit, OnDestroy {
  public endsInLabel = '';

  protected readonly currencyToSymbol = currencyToSymbol;

  data = input.required<IAuction>();

  private _countdownSubscription?: Subscription;
  private readonly _platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    this.updateCountdown();

    if (isPlatformBrowser(this._platformId)) {
      this._countdownSubscription = interval(1000).subscribe(() => {
        this.updateCountdown();
      });
    }
  }

  ngOnDestroy(): void {
    this._countdownSubscription?.unsubscribe();
  }

  get currentBidPrice(): number {
    const auction = this.data();
    return (
      auction?.highest_price ||
      auction?.Last_offer_price ||
      auction?.base_price ||
      0
    );
  }

  private updateCountdown(): void {
    const finishDate = this.data()?.finish_date;
    if (!finishDate) {
      this.endsInLabel = '';
      return;
    }

    const timeDifference = new Date(finishDate).getTime() - Date.now();
    if (timeDifference <= 0) {
      this.endsInLabel = '';
      return;
    }

    const totalHours = Math.floor(timeDifference / (1000 * 60 * 60));

    if (totalHours >= 24) {
      const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      this.endsInLabel = hours > 0 ? `${days}d ${hours}h` : `${days}d`;
      return;
    }

    const hours = totalHours;
    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
    this.endsInLabel = `${hours}h ${minutes}m`;
  }
}
